import React, { useEffect, useState } from 'react'
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Helmet from 'react-helmet'
import { Link } from 'react-router-dom'

import cogoToast from 'cogo-toast'
import moment from 'moment'

import AdjustersService from '../../../services/AdjustersService'
import AppointmentsService from '../../../services/AppointmentsService'
import CasesService from '../../../services/CasesService'
import ProviderService from '../../../services/ProviderService'
import ReferralSourcesService from '../../../services/ReferralSourcesService'
import TasksService from '../../../services/TasksService'
import UserService from '../../../services/UserService'
import { CAPABILITIES, Permissions } from '../../../utils/Permissions'
import CaseSummary from '../../CaseSummary'
import ConfirmationModal from '../../controls/ConfirmationModal'
import DatePicker from '../../controls/DatePicker'
import { DownloadButton } from '../../DownloadButton'
import FAIcon from '../../FAIcon'
import { OpenButton } from '../../OpenButton'

import InteractiveReportPreview from './components/InteractiveReportPreview'
import WYSIWYGEditModal from './components/WYSIWYGEditModal'

import './ReportPageTemplate.scss'

export default ({ caseNumber, isInProgress, actionsComponent, settings, caseReportPromise, filesPromise, user }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [caseReportModel, setCaseReportModel] = useState()
  const [allCaseAppointments, setAllCaseAppointments] = useState([])
  const [caseManager, setCaseManager] = useState()
  const [referralSource, setReferralSource] = useState()
  const [adjuster, setAdjuster] = useState()
  const [error, setError] = useState(null)

  const [showEditModal, setShowEditModal] = useState(false)
  const [editSequenceNumber, setEditSequenceNumber] = useState(0)
  const [editStartUtc, setEditStartUtc] = useState(null)
  const [editEndUtc, setEditEndUtc] = useState(null)
  const [editError, setEditError] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // WYSIWYG Editor State
  const [showWYSIWYGModal, setShowWYSIWYGModal] = useState(false)
  const [wysiwygFieldKey, setWysiwygFieldKey] = useState('')
  const [wysiwygFieldType, setWysiwygFieldType] = useState('')
  const [wysiwygFieldTitle, setWysiwygFieldTitle] = useState('')
  const [wysiwygCurrentValue, setWysiwygCurrentValue] = useState(null)
  const [wysiwygFocusField, setWysiwygFocusField] = useState(null)

  const [smartReviewSuggestions, setSmartReviewSuggestions] = useState([])
  const [isTriggeringReview, setIsTriggeringReview] = useState(false)
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [stickyTopOffset, setStickyTopOffset] = useState(185)
  const [reportReviewHours, setReportReviewHours] = useState(0.3)
  const [isLoggingReportTime, setIsLoggingReportTime] = useState(false)
  const [reportTimeLogged, setReportTimeLogged] = useState(false)

  useEffect(() => {
    const updateOffset = () => {
      const caseHeader = document.querySelector('.case-header-sticky-wrapper')
      const navbar = document.querySelector('.navbar')
      const navHeight = navbar ? navbar.offsetHeight : 72
      const headerHeight = caseHeader ? caseHeader.offsetHeight : 105
      setStickyTopOffset(navHeight + headerHeight + 8)
    }

    updateOffset()
    window.addEventListener('resize', updateOffset)
    return () => window.removeEventListener('resize', updateOffset)
  }, [])

  const statusLower = (caseReportModel?.status || '').toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-')
  const isEditableReport =
    isInProgress ||
    statusLower === 'in-progress' ||
    statusLower === 'inprogress' ||
    statusLower === 'draft' ||
    statusLower === 'submitted' ||
    statusLower === 'sent-to-administrator' ||
    statusLower === 'in-review' ||
    statusLower === 'under-review' ||
    statusLower === 'in-smart-review' ||
    statusLower === 'rejected'

  const handleCancelSmartReview = async () => {
    try {
      const refreshedReport = await CasesService.cancelSmartReview(caseNumber, caseReportModel.reportNumber)
      setCaseReportModel(refreshedReport)
      setSmartReviewSuggestions([])
      setActiveSuggestionIndex(0)
      cogoToast.success('Smart Review cancelled and reset.')
    } catch (err) {
      cogoToast.error('Failed to cancel Smart Review.')
    } finally {
      setShowCancelConfirm(false)
    }
  }

  const handleFieldClick = (fieldKey, fieldType, fieldTitle, currentValue, focusField = null) => {
    if (!isEditableReport) return // only edit if report is in an editable status

    if (fieldKey === 'report.metadata' || fieldKey === 'report.activityDates') {
      handleOpenEditModal()
      return
    }

    const currentSuggestion = smartReviewSuggestions[activeSuggestionIndex]
    let targetFocus = focusField
    if (!targetFocus && currentSuggestion) {
      const isMatchingField =
        currentSuggestion.fieldId === fieldKey ||
        (fieldKey && fieldKey.startsWith(currentSuggestion.fieldId)) ||
        (currentSuggestion.fieldId && currentSuggestion.fieldId.startsWith(fieldKey))
      const isDateRule =
        currentSuggestion.ruleName?.toLowerCase().includes('date') ||
        currentSuggestion.reason?.toLowerCase().includes('date')
      if (isMatchingField && isDateRule) {
        targetFocus = 'date'
      }
    }

    setWysiwygFocusField(targetFocus)
    setWysiwygFieldKey(fieldKey)
    setWysiwygFieldType(fieldType)
    setWysiwygFieldTitle(fieldTitle)
    setWysiwygCurrentValue(currentValue)
    setShowWYSIWYGModal(true)
  }

  const refreshReport = async () => {
    const refreshedReport = isInProgress
      ? await CasesService.getInProgressReport(caseNumber)
      : await CasesService.getReport(caseNumber, caseReportModel.reportNumber)
    setCaseReportModel(refreshedReport)
    try {
      const refreshedAppts = await AppointmentsService.getCaseAppointments(caseNumber)
      if (refreshedAppts) {
        setAllCaseAppointments(refreshedAppts)
      }
    } catch (e) {
      // ignore
    }
  }

  const findActivityParent = async actNum => {
    // 1. Check in-memory report appointments
    for (const appt of caseReportModel.appointments || []) {
      const found = (appt.activities || []).find(a => a.activityNumber === actNum)
      if (found) {
        return { parentType: 'appt', parentId: appt.appointmentNumber, appt, act: found }
      }
    }

    // 2. Check in-memory report tasks
    for (const task of caseReportModel.tasks || []) {
      const found = (task.activities || []).find(a => a.activityNumber === actNum)
      if (found) {
        return { parentType: 'task', parentId: task.taskNumber, task, act: found }
      }
    }

    // 3. Check report activities flat list & lookups
    const reportAct = (caseReportModel.activities || []).find(a => a.activityNumber === actNum)
    const apptNum = reportAct?.appointmentNumber || caseReportModel.activityToApptLookup?.[actNum]
    const taskNum = reportAct?.taskNumber || caseReportModel.activityToTaskLookup?.[actNum]

    if (apptNum) {
      try {
        const appt = await AppointmentsService.get(caseNumber, apptNum)
        const act = (appt.activities || []).find(a => a.activityNumber === actNum) || reportAct
        return { parentType: 'appt', parentId: apptNum, appt, act }
      } catch (e) {
        // ignore and continue
      }
    }

    if (taskNum) {
      try {
        const task = await TasksService.get(caseNumber, taskNum)
        const act = (task.activities || []).find(a => a.activityNumber === actNum) || reportAct
        return { parentType: 'task', parentId: taskNum, task, act }
      } catch (e) {
        // ignore and continue
      }
    }

    // 4. Query all case appointments and tasks for the case
    try {
      const [caseAppts, caseTasks] = await Promise.all([
        AppointmentsService.getCaseAppointments(caseNumber).catch(() => []),
        TasksService.getAllForCase(caseNumber).catch(() => []),
      ])

      for (const appt of caseAppts || []) {
        const found = (appt.activities || []).find(a => a.activityNumber === actNum)
        if (found) {
          return { parentType: 'appt', parentId: appt.appointmentNumber, appt, act: found }
        }
      }

      for (const task of caseTasks || []) {
        const found = (task.activities || []).find(a => a.activityNumber === actNum)
        if (found) {
          return { parentType: 'task', parentId: task.taskNumber, task, act: found }
        }
      }
    } catch (e) {
      // ignore
    }

    if (reportAct) {
      return { parentType: '', parentId: null, act: reportAct }
    }

    return null
  }

  const handleSaveWYSIWYG = async newVal => {
    try {
      setIsSaving(true)
      setShowWYSIWYGModal(false)
      const reportNum = caseReportModel.reportNumber || 0
      const currentCase = {
        ...caseReportModel.case,
        caseNumber: parseInt(caseNumber, 10),
      }

      if (wysiwygFieldKey === 'client.name') {
        const updatedCase = {
          ...currentCase,
          client: {
            ...currentCase.client,
            firstName: newVal.firstName,
            lastName: newVal.lastName,
          },
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'report.sequenceNumber') {
        const res = await CasesService.updateReport(caseNumber, reportNum, {
          sequenceNumber: parseInt(newVal, 10) || 1,
          reportType: caseReportModel.reportType,
        })
        setCaseReportModel(res)
      } else if (wysiwygFieldKey === 'report.overview') {
        const res = await CasesService.updateReport(caseNumber, reportNum, { overview: newVal })
        setCaseReportModel(res)
      } else if (wysiwygFieldKey === 'report.currentMedicalStatus') {
        const res = await CasesService.updateReport(caseNumber, reportNum, { currentMedicalStatus: newVal })
        setCaseReportModel(res)
      } else if (wysiwygFieldKey === 'report.nurseIntake') {
        const res = await CasesService.updateReport(caseNumber, reportNum, { nurseIntake: newVal })
        setCaseReportModel(res)
      } else if (wysiwygFieldKey === 'case.detailsForCaseManager') {
        const updatedCase = { ...currentCase, detailsForCaseManager: newVal }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'case.injury.notes') {
        const updatedCase = { ...currentCase, injury: { ...currentCase.injury, notes: newVal } }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'client.address') {
        const updatedCase = {
          ...currentCase,
          client: {
            ...currentCase.client,
            address: {
              ...currentCase.client?.address,
              line1: newVal.street,
              city: newVal.city,
              state: newVal.state,
              zip: newVal.zip,
            },
          },
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'client.phone') {
        const updatedCase = {
          ...currentCase,
          client: {
            ...currentCase.client,
            phone: newVal,
          },
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'client.birthdate') {
        const updatedCase = {
          ...currentCase,
          client: {
            ...currentCase.client,
            birthdate: newVal,
          },
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'case.injury.date') {
        const updatedCase = {
          ...currentCase,
          injury: {
            ...currentCase.injury,
            date: newVal,
          },
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'case.employer.occupation') {
        const updatedCase = {
          ...currentCase,
          employer: {
            ...currentCase.employer,
            occupation: newVal,
          },
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'case.employer') {
        const updatedCase = {
          ...currentCase,
          employer: {
            ...currentCase.employer,
            name: newVal?.name || '',
            address: {
              line1: newVal?.address?.street || newVal?.address?.line1 || '',
              city: newVal?.address?.city || '',
              state: newVal?.address?.state || '',
              zip: newVal?.address?.zip || '',
            },
            contact: {
              ...currentCase.employer?.contact,
              phone: newVal?.phone || '',
            },
          },
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'case.surgeries') {
        const existingList = currentCase.surgeriesAndHospitalizations || []
        const updatedList = newVal.map((item, idx) => {
          const original = existingList[idx] || {}
          return {
            ...original,
            dateOfSurgery: item.dateOfSurgery,
            title: item.title || 'Surgery',
            provider: item.provider || '',
          }
        })
        const updatedCase = {
          ...currentCase,
          surgeriesAndHospitalizations: updatedList,
        }
        await CasesService.update(updatedCase)

        await refreshReport()
      } else if (wysiwygFieldKey === 'case.injury.description' || wysiwygFieldKey === 'case.injury.descriptions') {
        let descList
        if (Array.isArray(newVal)) {
          descList = newVal.map(item => ({
            description: item.description || '',
            provider: item.provider || '',
            date: item.date || null,
          }))
        } else {
          descList = [...(currentCase.injury?.descriptions || [])]
          if (descList.length > 0) {
            const lastIdx = descList.length - 1
            descList[lastIdx] = {
              ...descList[lastIdx],
              description: newVal,
            }
          } else {
            descList.push({
              description: newVal,
              provider: '',
              date: null,
            })
          }
        }
        const updatedCase = {
          ...currentCase,
          injury: {
            ...currentCase.injury,
            descriptions: descList,
          },
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'case.systemsReview') {
        const updatedCase = {
          ...currentCase,
          systems: newVal,
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({
          ...caseReportModel,
          case: updatedCase,
          systemsReview: newVal,
        })
      } else if (wysiwygFieldKey === 'case.functionalStatuses') {
        const updatedCase = {
          ...currentCase,
          functional: newVal,
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({
          ...caseReportModel,
          case: updatedCase,
          functionalStatuses: newVal,
        })
      } else if (wysiwygFieldKey === 'case.treatmentProviders') {
        // Fetch current linked providers from db to ensure we have correct Guid IDs
        let dbProviders = []
        try {
          dbProviders = await ProviderService.getProvidersForCase(caseNumber)
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('Failed to fetch current linked providers for ID resolution', err)
        }

        const matchesProvider = (a, b) => {
          if (!a || !b) return false
          const aId = a.id || a.providerId
          const bId = b.id || b.providerId
          if (aId && bId) {
            return String(aId).toLowerCase() === String(bId).toLowerCase()
          }
          const aName = (a.name || a.organizationName || '').trim().toLowerCase()
          const bName = (b.name || b.organizationName || '').trim().toLowerCase()
          return Boolean(aName && bName && aName === bName)
        }

        // 1. Any dbProvider currently linked to the case that is NOT in newVal must be unlinked
        const toUnlink = dbProviders.filter(dbProv => !newVal.some(n => matchesProvider(dbProv, n)))

        // 2. Any provider in newVal that is NOT currently in dbProviders must be linked
        const toLink = newVal.filter(n => !dbProviders.some(dbProv => matchesProvider(dbProv, n)))

        for (const prov of toUnlink) {
          const targetId = prov.id || prov.providerId
          if (targetId) {
            await ProviderService.unlinkFromCase(caseNumber, targetId)
          } else {
            // eslint-disable-next-line no-console
            console.warn('Cannot unlink provider because no ID could be resolved:', prov)
          }
        }
        for (const prov of toLink) {
          let targetId = prov.id || prov.providerId
          if (!targetId) {
            try {
              const pName = prov.name || prov.organizationName
              if (pName) {
                const searchRes = await ProviderService.search({ query: pName, page: 1, pageSize: 5 })
                const items = searchRes?.items || searchRes || []
                const matched = items.find(
                  item =>
                    (item.name && item.name.trim().toLowerCase() === pName.trim().toLowerCase()) ||
                    (item.organizationName &&
                      item.organizationName.trim().toLowerCase() === pName.trim().toLowerCase()),
                )
                if (matched?.id) {
                  targetId = matched.id
                  prov.id = matched.id
                }
              }
            } catch (searchErr) {
              // eslint-disable-next-line no-console
              console.warn('Failed to search provider library for new provider ID', searchErr)
            }
          }

          if (targetId) {
            await ProviderService.linkToCase(caseNumber, targetId)
          } else {
            // eslint-disable-next-line no-console
            console.warn('Cannot link provider because no ID could be resolved:', prov)
          }
        }

        // 3. Persist any affiliation or unlink actions performed inside the Providers modal
        const findDbProvByEntry = (entry, fallbackName) => {
          if (!Array.isArray(dbProviders) || !dbProviders.length) return null
          const entryId = entry?.id || entry?.providerId
          if (entryId) {
            const byId = dbProviders.find(p => String(p.id).toLowerCase() === String(entryId).toLowerCase())
            if (byId) return byId
          }
          const cleanName = (fallbackName || entry?.name || '').split(' | ')[0].split(',')[0].trim().toLowerCase()
          if (!cleanName) return null
          return dbProviders.find(p => {
            const pName = (p.name || '').trim().toLowerCase()
            const pFull = `${p.firstName || ''} ${p.lastName || ''}`.trim().toLowerCase()
            const pOrg = (p.organizationName || '').trim().toLowerCase()
            return (
              pName === cleanName ||
              pFull === cleanName ||
              pOrg === cleanName ||
              (pFull && cleanName.includes(pFull)) ||
              (pName && cleanName.includes(pName))
            )
          })
        }

        for (const prov of newVal) {
          if (prov._affiliationAction === 'unlink') {
            const dbClinician = findDbProvByEntry(prov)
            if (dbClinician?.id) {
              await ProviderService.update(dbClinician.id, {
                ...dbClinician,
                classification: 'Individual',
                parentProviderId: null,
                organizationName: '',
                affiliations: [],
              })
            }
          } else if (prov._affiliationAction === 'affiliate' && prov.parentOrganization) {
            const dbClinician = findDbProvByEntry(prov)
            const dbTargetOrg = findDbProvByEntry({ id: prov.parentProviderId }, prov.parentOrganization)
            const resolvedOrgId = dbTargetOrg?.id || prov.parentProviderId || null
            if (dbClinician?.id) {
              const facName = prov.parentOrganization.trim()
              const existingAffs = Array.isArray(dbClinician.affiliations) ? dbClinician.affiliations : []
              const filteredAffs = existingAffs
                .filter(
                  a =>
                    (!resolvedOrgId ||
                      String(a.organizationId).toLowerCase() !== String(resolvedOrgId).toLowerCase()) &&
                    (!facName || (a.organizationName || '').trim().toLowerCase() !== facName.toLowerCase()),
                )
                .map(a => ({ ...a, isPrimary: false }))

              await ProviderService.update(dbClinician.id, {
                ...dbClinician,
                classification: 'Individual',
                parentProviderId: resolvedOrgId,
                organizationName: facName,
                affiliations: [
                  {
                    organizationId: resolvedOrgId,
                    organizationName: facName,
                    role: 'Primary Practice',
                    isPrimary: true,
                  },
                  ...filteredAffs,
                ],
              })
            }
          }
        }

        const sanitizedProviders = newVal.map(p => {
          const copy = { ...p }
          delete copy._affiliationAction
          return copy
        })

        const updatedCase = {
          ...currentCase,
          treatmentProviders: sanitizedProviders,
          providers: sanitizedProviders,
        }
        await CasesService.update(updatedCase)

        setCaseReportModel(prev => ({
          ...prev,
          case: {
            ...(prev?.case || {}),
            treatmentProviders: sanitizedProviders,
            providers: sanitizedProviders,
          },
          treatmentProviders: sanitizedProviders,
        }))

        await refreshReport()
      } else if (wysiwygFieldKey === 'case.allergies') {
        const updatedCase = {
          ...currentCase,
          allergies: newVal,
        }
        await CasesService.update(updatedCase)
        setCaseReportModel(prev => ({
          ...prev,
          case: updatedCase,
        }))
        await refreshReport()
      } else if (wysiwygFieldKey === 'case.medications') {
        const updatedCase = {
          ...currentCase,
          medications: newVal,
        }
        await CasesService.update(updatedCase)
        setCaseReportModel(prev => ({
          ...prev,
          case: updatedCase,
        }))
        await refreshReport()
      } else if (wysiwygFieldKey === 'case.diagnoses') {
        const updatedCase = {
          ...currentCase,
          diagnosis: newVal.map(d => ({
            name: d.name,
            physician: d.physician,
            date: d.date,
          })),
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'case.transportation') {
        const updatedCase = {
          ...currentCase,
          transportation: newVal.map(t => ({
            providerId: t.providerId,
            providerName: t.providerName,
          })),
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({
          ...caseReportModel,
          case: updatedCase,
          transportation: newVal,
        })
      } else if (wysiwygFieldKey === 'case.translationServices') {
        const updatedCase = {
          ...currentCase,
          translationServices: newVal.map(t => ({
            providerId: t.providerId,
            providerName: t.providerName,
          })),
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({
          ...caseReportModel,
          case: updatedCase,
          translationServices: newVal,
        })
      } else if (wysiwygFieldKey === 'referral.adjusterId') {
        const updatedCase = {
          ...currentCase,
          referral: {
            ...currentCase.referral,
            adjusterId: newVal ? parseInt(newVal, 10) : null,
          },
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
        if (newVal) {
          const adj = await AdjustersService.get(newVal)
          setAdjuster(adj)
        } else {
          setAdjuster(null)
        }
      } else if (wysiwygFieldKey === 'case.defendant') {
        const updatedCase = {
          ...currentCase,
          defendant: {
            ...currentCase.defendant,
            name: newVal?.name || '',
            address: {
              line1: newVal?.address?.line1 || newVal?.address?.street || '',
              city: newVal?.address?.city || '',
              state: newVal?.address?.state || '',
              zip: newVal?.address?.zip || '',
            },
            contact: {
              ...currentCase.defendant?.contact,
              phone: newVal?.phone || newVal?.contact?.phone || '',
            },
          },
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'case.plaintiff') {
        const updatedCase = {
          ...currentCase,
          plaintiff: {
            ...currentCase.plaintiff,
            name: newVal?.name || '',
            address: {
              line1: newVal?.address?.line1 || newVal?.address?.street || '',
              city: newVal?.address?.city || '',
              state: newVal?.address?.state || '',
              zip: newVal?.address?.zip || '',
            },
            contact: {
              ...currentCase.plaintiff?.contact,
              phone: newVal?.phone || newVal?.contact?.phone || '',
            },
          },
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'case.diagnosis') {
        const diagList = [...(currentCase.diagnosis || [])]
        if (diagList.length > 0) {
          diagList[0] = {
            name: newVal.name,
            physician: newVal.physician,
            date: newVal.date,
          }
        } else {
          diagList.push({
            name: newVal.name,
            physician: newVal.physician,
            date: newVal.date,
          })
        }
        const updatedCase = { ...currentCase, diagnosis: diagList }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'report.invoiceNumber') {
        const res = await CasesService.updateReport(caseNumber, reportNum, { invoiceNumber: newVal })
        setCaseReportModel(res)
      } else if (wysiwygFieldKey === 'referral.claimNumber') {
        const updatedCase = { ...currentCase, referral: { ...currentCase.referral, claimNumber: newVal } }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'referral.referralType') {
        const updatedCase = { ...currentCase, referral: { ...currentCase.referral, referralType: newVal } }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'referral.date') {
        const updatedCase = { ...currentCase, referral: { ...currentCase.referral, date: newVal } }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'case.status') {
        // Work status editing
        const history = [...(currentCase.status?.history || [])]
        if (history.length > 0) {
          const index = history.length - 1
          history[index] = {
            ...history[index],
            date: newVal.date,
            treatmentProvider: newVal.treatmentProvider,
            workStatus: newVal.status,
          }
        } else {
          history.push({
            date: newVal.date,
            treatmentProvider: newVal.treatmentProvider,
            workStatus: newVal.status,
          })
        }
        const updatedCase = { ...currentCase, status: { ...currentCase.status, history } }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey === 'case.mmi') {
        const history = [...(currentCase.mmi?.history || [])]
        if (history.length > 0) {
          const index = history.length - 1
          history[index] = {
            ...history[index],
            date: newVal.date,
            isActual: newVal.isActual,
            narrative: newVal.narrative,
          }
        } else {
          history.push({
            date: newVal.date,
            isActual: newVal.isActual,
            narrative: newVal.narrative,
          })
        }
        const updatedCase = { ...currentCase, mmi: { ...currentCase.mmi, history } }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (wysiwygFieldKey.startsWith('appointment.')) {
        const parts = wysiwygFieldKey.split('.')
        const apptNum = parseInt(parts[1], 10)
        const isPlural = parts[2] === 'noteId'
        const isDesc = parts[2] === 'description'
        const isDetails = parts[2] === 'details' || !parts[2]

        let originalAppt = (caseReportModel.appointments || []).find(a => a.appointmentNumber === apptNum)
        if (!originalAppt && Array.isArray(allCaseAppointments)) {
          originalAppt = allCaseAppointments.find(a => a.appointmentNumber === apptNum)
        }
        if (!originalAppt && Array.isArray(caseReportModel.upcomingAppointments)) {
          originalAppt = caseReportModel.upcomingAppointments.find(a => a.appointmentNumber === apptNum)
        }
        if (!originalAppt) {
          try {
            originalAppt = await AppointmentsService.get(caseNumber, apptNum)
          } catch (e) {
            // ignore
          }
        }

        if (originalAppt || isDetails) {
          let updatedAppt
          if (isDetails && typeof newVal === 'object') {
            const startUtc = newVal.date ? moment.utc(newVal.date).toISOString() : originalAppt?.startUtc
            updatedAppt = {
              ...(originalAppt || {}),
              title: newVal.title,
              startUtc,
              provider: newVal.provider,
              description: newVal.description,
              note: newVal.note,
            }
          } else if (isPlural) {
            const noteId = parseInt(parts[3], 10)
            const updatedNotes = ((originalAppt && originalAppt.notes) || []).map(n => {
              if (n.id === noteId) {
                return { ...n, note: newVal }
              }
              return n
            })
            updatedAppt = { ...originalAppt, notes: updatedNotes }
          } else if (isDesc) {
            updatedAppt = { ...originalAppt, description: newVal }
          } else {
            updatedAppt = { ...originalAppt, note: newVal }
          }
          await AppointmentsService.update(caseNumber, apptNum, updatedAppt)
          setAllCaseAppointments(prev =>
            (prev || []).map(a => (a.appointmentNumber === apptNum ? { ...a, ...updatedAppt } : a)),
          )
          await refreshReport()
        }
      } else if (wysiwygFieldKey.startsWith('activity.')) {
        const parts = wysiwygFieldKey.split('.')
        const isNew = parts[1] === 'new'

        if (isNew) {
          const parentType = parts[2] // 'appt' or 'task'
          const parentId = parseInt(parts[3], 10)
          if (parentType === 'appt') {
            const originalAppt = caseReportModel.appointments.find(a => a.appointmentNumber === parentId)
            if (originalAppt) {
              const newAct = {
                activityNumber: 0,
                date: newVal.date,
                type: newVal.type,
                time: newVal.time,
                description: newVal.description,
              }
              const updatedAppt = {
                ...originalAppt,
                activities: [...(originalAppt.activities || []), newAct],
              }
              await AppointmentsService.update(caseNumber, parentId, updatedAppt)
            }
          } else if (parentType === 'task') {
            const originalTask = caseReportModel.tasks.find(t => t.taskNumber === parentId)
            if (originalTask) {
              const newAct = {
                activityNumber: 0,
                date: newVal.date,
                type: newVal.type,
                time: newVal.time,
                description: newVal.description,
              }
              const updatedTask = {
                ...originalTask,
                activities: [...(originalTask.activities || []), newAct],
              }
              await TasksService.update(updatedTask, caseNumber, parentId)
            }
          }
        } else {
          const actNum = parseInt(parts[1], 10)
          if (wysiwygFieldKey.includes('.appt.')) {
            const apptNum = parseInt(parts[3], 10)
            let originalAppt = (caseReportModel.appointments || []).find(a => a.appointmentNumber === apptNum)
            if (!originalAppt || !(originalAppt.activities || []).some(act => act.activityNumber === actNum)) {
              try {
                originalAppt = await AppointmentsService.get(caseNumber, apptNum)
              } catch (e) {
                // ignore
              }
            }
            if (originalAppt) {
              let updatedActivities
              if (newVal?._delete) {
                updatedActivities = (originalAppt.activities || []).filter(act => act.activityNumber !== actNum)
              } else {
                updatedActivities = (originalAppt.activities || []).map(act => {
                  if (act.activityNumber === actNum) {
                    return {
                      ...act,
                      date: newVal.date,
                      type: newVal.type,
                      time: newVal.time,
                      description: newVal.description,
                    }
                  }
                  return act
                })
              }
              const updatedAppt = { ...originalAppt, activities: updatedActivities }
              await AppointmentsService.update(caseNumber, apptNum, updatedAppt)
            }
          } else if (wysiwygFieldKey.includes('.task.')) {
            const taskNum = parseInt(parts[3], 10)
            let originalTask = (caseReportModel.tasks || []).find(t => t.taskNumber === taskNum)
            if (!originalTask || !(originalTask.activities || []).some(act => act.activityNumber === actNum)) {
              try {
                originalTask = await TasksService.get(caseNumber, taskNum)
              } catch (e) {
                // ignore
              }
            }
            if (originalTask) {
              let updatedActivities
              if (newVal?._delete) {
                updatedActivities = (originalTask.activities || []).filter(act => act.activityNumber !== actNum)
              } else {
                updatedActivities = (originalTask.activities || []).map(act => {
                  if (act.activityNumber === actNum) {
                    return {
                      ...act,
                      date: newVal.date,
                      type: newVal.type,
                      time: newVal.time,
                      description: newVal.description,
                    }
                  }
                  return act
                })
              }
              const updatedTask = { ...originalTask, activities: updatedActivities }
              await TasksService.update(updatedTask, caseNumber, taskNum)
            }
          } else {
            const apptNum = caseReportModel.activityToApptLookup?.[actNum]
            const taskNum = caseReportModel.activityToTaskLookup?.[actNum]
            let originalAppt = (caseReportModel.appointments || []).find(
              a => a.appointmentNumber === apptNum || (a.activities || []).some(act => act.activityNumber === actNum),
            )
            if (!originalAppt && apptNum) {
              try {
                originalAppt = await AppointmentsService.get(caseNumber, apptNum)
              } catch (e) {
                // ignore
              }
            }
            if (originalAppt) {
              let updatedActivities
              if (newVal?._delete) {
                updatedActivities = (originalAppt.activities || []).filter(act => act.activityNumber !== actNum)
              } else {
                updatedActivities = (originalAppt.activities || []).map(act => {
                  if (act.activityNumber === actNum) {
                    return {
                      ...act,
                      date: newVal.date,
                      type: newVal.type,
                      time: newVal.time,
                      description: newVal.description,
                    }
                  }
                  return act
                })
              }
              const updatedAppt = { ...originalAppt, activities: updatedActivities }
              await AppointmentsService.update(caseNumber, originalAppt.appointmentNumber, updatedAppt)
            } else {
              let originalTask = (caseReportModel.tasks || []).find(
                t => t.taskNumber === taskNum || (t.activities || []).some(act => act.activityNumber === actNum),
              )
              if (!originalTask && taskNum) {
                try {
                  originalTask = await TasksService.get(caseNumber, taskNum)
                } catch (e) {
                  // ignore
                }
              }
              if (originalTask) {
                let updatedActivities
                if (newVal?._delete) {
                  updatedActivities = (originalTask.activities || []).filter(act => act.activityNumber !== actNum)
                } else {
                  updatedActivities = (originalTask.activities || []).map(act => {
                    if (act.activityNumber === actNum) {
                      return {
                        ...act,
                        date: newVal.date,
                        type: newVal.type,
                        time: newVal.time,
                        description: newVal.description,
                      }
                    }
                    return act
                  })
                }
                const updatedTask = { ...originalTask, activities: updatedActivities }
                await TasksService.update(updatedTask, caseNumber, originalTask.taskNumber)
              } else {
                const parentInfo = await findActivityParent(actNum)
                if (parentInfo?.parentType === 'appt' && parentInfo.appt) {
                  let updatedActivities
                  if (newVal?._delete) {
                    updatedActivities = (parentInfo.appt.activities || []).filter(act => act.activityNumber !== actNum)
                  } else {
                    updatedActivities = (parentInfo.appt.activities || []).map(act => {
                      if (act.activityNumber === actNum) {
                        return {
                          ...act,
                          date: newVal.date,
                          type: newVal.type,
                          time: newVal.time,
                          description: newVal.description,
                        }
                      }
                      return act
                    })
                  }
                  await AppointmentsService.update(caseNumber, parentInfo.parentId, {
                    ...parentInfo.appt,
                    activities: updatedActivities,
                  })
                } else if (parentInfo?.parentType === 'task' && parentInfo.task) {
                  let updatedActivities
                  if (newVal?._delete) {
                    updatedActivities = (parentInfo.task.activities || []).filter(act => act.activityNumber !== actNum)
                  } else {
                    updatedActivities = (parentInfo.task.activities || []).map(act => {
                      if (act.activityNumber === actNum) {
                        return {
                          ...act,
                          date: newVal.date,
                          type: newVal.type,
                          time: newVal.time,
                          description: newVal.description,
                        }
                      }
                      return act
                    })
                  }
                  await TasksService.update(
                    { ...parentInfo.task, activities: updatedActivities },
                    caseNumber,
                    parentInfo.parentId,
                  )
                }
              }
            }
          }
        }

        await refreshReport()
      } else if (wysiwygFieldKey.startsWith('expense.')) {
        const parts = wysiwygFieldKey.split('.')
        const expNum = parseInt(parts[1], 10)

        if (wysiwygFieldKey.includes('.appt.')) {
          const apptNum = parseInt(parts[3], 10)
          const originalAppt = caseReportModel.appointments.find(a => a.appointmentNumber === apptNum)
          if (originalAppt) {
            let updatedExpenses
            if (newVal?._delete) {
              updatedExpenses = (originalAppt.expenses || []).filter(exp => exp.expenseNumber !== expNum)
            } else {
              updatedExpenses = (originalAppt.expenses || []).map(exp => {
                if (exp.expenseNumber === expNum) {
                  return {
                    ...exp,
                    date: newVal.date,
                    type: newVal.type,
                    amount: newVal.amount,
                    distance: newVal.distance,
                    description: newVal.description,
                  }
                }
                return exp
              })
            }
            const updatedAppt = { ...originalAppt, expenses: updatedExpenses }
            await AppointmentsService.update(caseNumber, apptNum, updatedAppt)
          }
        } else if (wysiwygFieldKey.includes('.task.')) {
          const taskNum = parseInt(parts[3], 10)
          const originalTask = caseReportModel.tasks.find(t => t.taskNumber === taskNum)
          if (originalTask) {
            let updatedExpenses
            if (newVal?._delete) {
              updatedExpenses = (originalTask.expenses || []).filter(exp => exp.expenseNumber !== expNum)
            } else {
              updatedExpenses = (originalTask.expenses || []).map(exp => {
                if (exp.expenseNumber === expNum) {
                  return {
                    ...exp,
                    date: newVal.date,
                    type: newVal.type,
                    amount: newVal.amount,
                    distance: newVal.distance,
                    description: newVal.description,
                  }
                }
                return exp
              })
            }
            const updatedTask = { ...originalTask, expenses: updatedExpenses }
            await TasksService.update(updatedTask, caseNumber, taskNum)
          }
        }

        await refreshReport()
      } else {
        throw new Error(`Updating ${wysiwygFieldTitle || wysiwygFieldKey} is not supported.`)
      }

      // Check if this edit resolves an active smart review suggestion (with loose matching)
      const matchingSuggestionIndex = smartReviewSuggestions.findIndex(s => {
        if (s.resolved) return false
        const id = (s.fieldId || '').toLowerCase()
        const key = (wysiwygFieldKey || '').toLowerCase()
        return (
          id === key ||
          (key === 'client.name' &&
            (id.includes('client.firstname') ||
              id.includes('client.lastname') ||
              id.includes('client.name') ||
              id.includes('claimant'))) ||
          (key === 'client.address' && id.includes('client.address')) ||
          (key === 'case.mmi' && id.includes('mmi')) ||
          (key === 'case.status' && id.includes('status')) ||
          (key === 'case.allergies' && id.includes('allerg')) ||
          (key === 'case.medications' && id.includes('medicat')) ||
          (key === 'case.diagnoses' && id.includes('diagnos')) ||
          id.includes(key) ||
          key.includes(id)
        )
      })
      if (matchingSuggestionIndex !== -1) {
        const suggestion = smartReviewSuggestions[matchingSuggestionIndex]
        try {
          let finalTextStr = typeof newVal === 'string' ? newVal : JSON.stringify(newVal)
          if (wysiwygFieldKey === 'client.name') {
            finalTextStr = `${newVal.firstName} ${newVal.lastName}`
          }
          await CasesService.auditSmartReviewSuggestion(caseNumber, caseReportModel.reportNumber, {
            fieldId: suggestion.fieldId,
            suggestionsType: suggestion.suggestionsType,
            ruleName: suggestion.ruleName,
            originalText: suggestion.originalText,
            proposedText: suggestion.proposedText,
            finalText: finalTextStr,
            isOverridden: true,
          })
          markSuggestionResolved(matchingSuggestionIndex, 'edited')
          cogoToast.success('Review suggestion updated and audited.')
        } catch (auditErr) {
          // eslint-disable-next-line no-console
          console.error('Failed to log edit audit:', auditErr)
        }
      } else {
        cogoToast.success(`${wysiwygFieldTitle} updated successfully`)
      }
    } catch (err) {
      cogoToast.error(err.message || 'Failed to update field')
    } finally {
      setIsSaving(false)
    }
  }

  const handleLogReportReviewTime = async () => {
    const hours = Math.round(Number(reportReviewHours) * 10) / 10
    if (!hours || hours <= 0) return
    setIsLoggingReportTime(true)
    try {
      const seqNum = caseReportModel?.sequenceNumber || 1
      await CasesService.logBillableActivity(caseNumber, {
        type: 'Report',
        time: hours,
        date: moment().format('YYYY-MM-DD'),
        description: `Progress Report #${seqNum} preparation, clinical synthesis, and Smart Review verification.`,
      })
      setReportTimeLogged(true)
      cogoToast.success(`Logged ${hours.toFixed(1)}h Report activity`)
      await refreshReport()
    } catch (_err) {
      cogoToast.error('Failed to log Report billable time')
    } finally {
      setIsLoggingReportTime(false)
    }
  }

  const handleOpenEditModal = () => {
    setEditSequenceNumber(caseReportModel.sequenceNumber || 0)
    setEditStartUtc(
      caseReportModel.fromDateTimeUtc ? moment.utc(caseReportModel.fromDateTimeUtc).format('YYYY-MM-DD') : null,
    )
    setEditEndUtc(caseReportModel.toDateTimeUtc ? moment.utc(caseReportModel.toDateTimeUtc).format('YYYY-MM-DD') : null)
    setEditError('')
    setShowEditModal(true)
  }

  const handleSaveMetadata = () => {
    const referralDate = caseReportModel.case?.referral?.date
    if (referralDate && moment.utc(editStartUtc).isBefore(moment.utc(referralDate), 'day')) {
      setEditError(
        `Start Date must be on or after the Referral Date (${moment.utc(referralDate).format('MM/DD/YYYY')}).`,
      )
      return
    }
    if (moment.utc(editEndUtc).isBefore(moment.utc(editStartUtc), 'day')) {
      setEditError('End Date must be on or after the Start Date.')
      return
    }

    setEditError('')
    setIsSaving(true)

    const updateData = {
      sequenceNumber: caseReportModel.reportType === 'Progress' ? parseInt(editSequenceNumber, 10) || 0 : undefined,
      startUtc: editStartUtc ? moment.utc(editStartUtc).startOf('day').format() : null,
      endUtc: editEndUtc ? moment.utc(editEndUtc).endOf('day').format() : null,
      reportType: caseReportModel.reportType,
    }

    const reportNum = caseReportModel.reportNumber || 0

    CasesService.updateReport(caseNumber, reportNum, updateData)
      .onSuccess(updatedReport => {
        setCaseReportModel(updatedReport)
        setShowEditModal(false)
      })
      .onBadRequest(err => {
        setEditError(err || 'Validation failed.')
      })
      .onStatus(403, () => setEditError('You do not have permission to update this report.'))
      .catch(_err => setEditError('An unexpected error occurred while saving.'))
      .then(() => setIsSaving(false))
  }

  const handleTriggerSmartReview = () => {
    setIsTriggeringReview(true)
    CasesService.triggerSmartReview(caseNumber, caseReportModel.reportNumber)
      .onSuccess(result => {
        setCaseReportModel(result)
        try {
          if (result.smartReviewSuggestions) {
            setSmartReviewSuggestions(JSON.parse(result.smartReviewSuggestions).suggestions || [])
          } else {
            setSmartReviewSuggestions([])
          }
        } catch (e) {
          setSmartReviewSuggestions([])
        }
        cogoToast.success('AI Smart Review complete!')
      })
      .onBadRequest(err => {
        const cleanErr = typeof err === 'string' ? err.replace(/\n+/g, ' — ') : 'AI Smart Review failed.'
        cogoToast.error(cleanErr)
      })
      .onStatus(403, () => cogoToast.error('You do not have permission to run AI Smart Review.'))
      .onStatus(500, () => cogoToast.error('AI Review failed. Ensure Bedrock service is configured.'))
      .catch(err => {
        cogoToast.error(err.message || 'An unexpected error occurred during AI review.')
      })
      .then(() => {
        setIsTriggeringReview(false)
      })
  }

  const markSuggestionResolved = (index, outcome) => {
    setSmartReviewSuggestions(prev => {
      const copy = [...prev]
      copy[index] = { ...copy[index], resolved: true, outcome }
      // Auto-advance to the next unresolved suggestion
      const nextUnresolved = copy.findIndex((s, idx) => idx > index && !s.resolved)
      if (nextUnresolved !== -1) {
        setActiveSuggestionIndex(nextUnresolved)
      } else {
        const prevUnresolved = copy.findIndex((s, idx) => idx < index && !s.resolved)
        if (prevUnresolved !== -1) {
          setActiveSuggestionIndex(prevUnresolved)
        }
      }
      return copy
    })
  }

  const handleEditActivityFromSuggestion = async suggestion => {
    if (!suggestion.fieldId?.startsWith('activity.')) return
    const actNum = parseInt(suggestion.fieldId.replace('activity.', '').split('.')[0], 10)
    if (isNaN(actNum)) return

    const parentInfo = await findActivityParent(actNum)

    if (parentInfo && parentInfo.act) {
      const clickKey =
        parentInfo.parentType && parentInfo.parentId
          ? `activity.${actNum}.${parentInfo.parentType}.${parentInfo.parentId}`
          : `activity.${actNum}`
      const isDateWarning =
        suggestion.ruleName?.toLowerCase().includes('date') || suggestion.reason?.toLowerCase().includes('date')
      handleFieldClick(
        clickKey,
        'activity',
        `Activity - ${parentInfo.act.type || 'Details'}`,
        parentInfo.act,
        isDateWarning ? 'date' : 'description',
      )
    } else {
      cogoToast.info('Activity could not be located in report appointments or tasks.')
    }
  }

  const handleIgnoreSuggestion = async (suggestion, index) => {
    try {
      await CasesService.auditSmartReviewSuggestion(caseNumber, caseReportModel.reportNumber, {
        fieldId: suggestion.fieldId,
        suggestionsType: suggestion.suggestionsType,
        ruleName: suggestion.ruleName,
        originalText: suggestion.originalText,
        proposedText: suggestion.proposedText,
        finalText: null,
        isOverridden: true,
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to audit smart review ignore action', err)
    }
    markSuggestionResolved(index, 'ignored')
    cogoToast.success('Suggestion ignored.')
  }

  const handleAcceptSuggestion = async (suggestion, index) => {
    try {
      const parsedCaseNumber = parseInt(caseNumber, 10)
      if (suggestion.fieldId === 'report.overview') {
        const reportNum = caseReportModel.reportNumber || 0
        await CasesService.updateReport(caseNumber, reportNum, { overview: suggestion.proposedText })
      } else if (suggestion.fieldId === 'case.injury.description' && suggestion.proposedText) {
        const descList = [...(caseReportModel.case?.injury?.descriptions || [])]
        if (descList.length > 0) {
          const matchIdx = suggestion.originalText
            ? descList.findIndex(d => (d?.description || '').includes(suggestion.originalText))
            : -1
          const targetIdx = matchIdx !== -1 ? matchIdx : descList.length - 1
          const curr = descList[targetIdx].description || ''
          const updatedText =
            suggestion.originalText && curr.includes(suggestion.originalText)
              ? curr.replace(suggestion.originalText, suggestion.proposedText)
              : suggestion.proposedText
          descList[targetIdx] = {
            ...descList[targetIdx],
            description: updatedText,
          }
        } else {
          descList.push({
            description: suggestion.proposedText,
            provider: '',
            date: null,
          })
        }
        const updatedCase = {
          ...caseReportModel.case,
          caseNumber: parsedCaseNumber,
          injury: {
            ...caseReportModel.case?.injury,
            descriptions: descList,
          },
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (
        suggestion.ruleName === 'Negative Finding' ||
        suggestion.ruleName === 'Remove Negative Finding' ||
        suggestion.ruleName === 'Pre-Existing Condition' ||
        (suggestion.fieldId?.startsWith('diagnosis.') && suggestion.proposedText === 'Remove') ||
        (suggestion.fieldId?.startsWith('diagnosis.') &&
          suggestion.suggestionsType === 'warning' &&
          !suggestion.proposedText)
      ) {
        const rawDiags = caseReportModel.case?.diagnosis || []
        const fieldIdSuffix = suggestion.fieldId?.replace('diagnosis.', '') || ''
        const isIndex = /^\d+$/.test(fieldIdSuffix)
        const targetIndex = isIndex ? parseInt(fieldIdSuffix, 10) : -1

        const toRemove = (suggestion.originalText || (!isIndex ? fieldIdSuffix : ''))
          .toLowerCase()
          .replace(/^['"`]+|['"`]+$/g, '')
          .trim()

        const updatedDiags = rawDiags.filter((d, idx) => {
          if (targetIndex !== -1 && idx === targetIndex) return false
          const dName = (d.name || d.description || (typeof d === 'string' ? d : '')).toLowerCase().trim()
          if (toRemove && (dName === toRemove || dName.includes(toRemove) || toRemove.includes(dName))) return false
          return true
        })
        const updatedCase = { ...caseReportModel.case, caseNumber: parsedCaseNumber, diagnosis: updatedDiags }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (suggestion.ruleName === 'Consolidate Diagnoses') {
        const rawDiags = caseReportModel.case?.diagnosis || []
        const toReplace = (suggestion.originalText || '')
          .toLowerCase()
          .split(';')
          .map(s => s.trim().replace(/^['"`]+|['"`]+$/g, ''))
          .filter(Boolean)
        const remaining = rawDiags.filter(d => {
          const dName = (d.name || d.description || (typeof d === 'string' ? d : '')).toLowerCase().trim()
          return !toReplace.some(tr => tr && (dName.includes(tr) || tr.includes(dName)))
        })
        const canonical = {
          name: suggestion.proposedText,
          description: suggestion.proposedText,
        }
        const updatedCase = {
          ...caseReportModel.case,
          caseNumber: parsedCaseNumber,
          diagnosis: [...remaining, canonical],
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (
        suggestion.ruleName === 'Allergy Contradiction' ||
        suggestion.ruleName === 'Remove Contradictory Allergy' ||
        (suggestion.fieldId === 'case.allergies' &&
          (suggestion.proposedText === 'Delete' || suggestion.proposedText === 'Remove'))
      ) {
        const rawAllergies = caseReportModel.case?.allergies || []
        const toRemove = (suggestion.originalText || '')
          .toLowerCase()
          .replace(/^['"`]+|['"`]+$/g, '')
          .trim()
        const isNegativeAssertion = text => {
          const t = (text || '').toLowerCase().trim()
          return (
            t === 'no known drug allergies' ||
            t === 'no known allergies' ||
            t === 'no allergies' ||
            t === 'nkda' ||
            t === 'none'
          )
        }
        const updatedAllergies = rawAllergies
          .filter(a => {
            const aText = (typeof a === 'string' ? a : a.allergy || a.name || a.description || '').toLowerCase().trim()
            if (toRemove && (aText === toRemove || aText.includes(toRemove) || toRemove.includes(aText))) {
              return false
            }
            if (suggestion.ruleName?.includes('Allergy') && isNegativeAssertion(aText)) {
              return false
            }
            return true
          })
          .map(a => {
            if (typeof a === 'string') {
              return { name: a.trim() }
            }
            return {
              name: a.name || a.allergy || a.description || '',
              ...(a.reaction ? { reaction: a.reaction } : {}),
            }
          })
          .filter(a => Boolean(a.name))
        const updatedCase = { ...caseReportModel.case, caseNumber: parsedCaseNumber, allergies: updatedAllergies }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      } else if (
        suggestion.ruleName === 'Incomplete Activity' ||
        (suggestion.fieldId?.startsWith('activity.') && suggestion.proposedText === 'Remove')
      ) {
        const actNum = parseInt(suggestion.fieldId.replace('activity.', '').split('.')[0], 10)
        const parentInfo = await findActivityParent(actNum)
        if (parentInfo?.parentType === 'appt' && parentInfo.appt) {
          const updatedActs = (parentInfo.appt.activities || []).filter(act => act.activityNumber !== actNum)
          await AppointmentsService.update(caseNumber, parentInfo.parentId, {
            ...parentInfo.appt,
            activities: updatedActs,
          })
        } else if (parentInfo?.parentType === 'task' && parentInfo.task) {
          const updatedActs = (parentInfo.task.activities || []).filter(act => act.activityNumber !== actNum)
          await TasksService.update({ ...parentInfo.task, activities: updatedActs }, caseNumber, parentInfo.parentId)
        }
      } else if (
        suggestion.fieldId?.startsWith('activity.') &&
        suggestion.proposedText &&
        suggestion.ruleName?.toLowerCase().includes('description') &&
        !/^verify\b|^check\b/i.test(suggestion.proposedText)
      ) {
        const actNum = parseInt(suggestion.fieldId.replace('activity.', '').split('.')[0], 10)
        const parentInfo = await findActivityParent(actNum)
        if (parentInfo?.parentType === 'appt' && parentInfo.appt) {
          const updatedActs = (parentInfo.appt.activities || []).map(act =>
            act.activityNumber === actNum ? { ...act, description: suggestion.proposedText } : act,
          )
          await AppointmentsService.update(caseNumber, parentInfo.parentId, {
            ...parentInfo.appt,
            activities: updatedActs,
          })
        } else if (parentInfo?.parentType === 'task' && parentInfo.task) {
          const updatedActs = (parentInfo.task.activities || []).map(act =>
            act.activityNumber === actNum ? { ...act, description: suggestion.proposedText } : act,
          )
          await TasksService.update({ ...parentInfo.task, activities: updatedActs }, caseNumber, parentInfo.parentId)
        }
      } else if (suggestion.ruleName === 'Provider Merge') {
        const rawProvs = caseReportModel.case?.providers || []
        const toMerge = (suggestion.originalText || '')
          .toLowerCase()
          .split(';')
          .map(s => s.trim().replace(/^['"`]+|['"`]+$/g, ''))
          .filter(Boolean)
        const remaining = rawProvs.filter(p => {
          const pName = (p.name || p.practiceName || '').toLowerCase().trim()
          return !toMerge.some(tm => tm && (pName.includes(tm) || tm.includes(pName)))
        })
        const canonicalProv = {
          name: suggestion.proposedText,
          providerType: 'Treating Provider',
        }
        const updatedCase = {
          ...caseReportModel.case,
          caseNumber: parsedCaseNumber,
          providers: [...remaining, canonicalProv],
        }
        await CasesService.update(updatedCase)
        setCaseReportModel({ ...caseReportModel, case: updatedCase })
      }
      try {
        await CasesService.auditSmartReviewSuggestion(caseNumber, caseReportModel.reportNumber, {
          fieldId: suggestion.fieldId,
          suggestionsType: suggestion.suggestionsType,
          ruleName: suggestion.ruleName,
          originalText: suggestion.originalText,
          proposedText: suggestion.proposedText,
          finalText: suggestion.proposedText,
          isOverridden: false,
        })
      } catch (auditErr) {
        // eslint-disable-next-line no-console
        console.warn('Failed to audit smart review suggestion', auditErr)
      }
      markSuggestionResolved(index, 'applied')
      await refreshReport()
      cogoToast.success('Suggestion applied!')
    } catch (err) {
      cogoToast.error('Failed to apply suggestion.')
    }
  }

  const handleRemoveDiagnosisSuggestion = async (suggestion, index) => {
    try {
      const parsedCaseNumber = parseInt(caseNumber, 10)
      const rawDiags = caseReportModel.case?.diagnosis || []
      const fieldIdSuffix = suggestion.fieldId?.replace('diagnosis.', '') || ''
      const isIndex = /^\d+$/.test(fieldIdSuffix)
      const targetIndex = isIndex ? parseInt(fieldIdSuffix, 10) : -1

      const toRemove = (suggestion.originalText || (!isIndex ? fieldIdSuffix : ''))
        .toLowerCase()
        .replace(/^['"`]+|['"`]+$/g, '')
        .trim()

      const updatedDiags = rawDiags.filter((d, idx) => {
        if (targetIndex !== -1 && idx === targetIndex) return false
        const dName = (d.name || d.description || (typeof d === 'string' ? d : '')).toLowerCase().trim()
        if (toRemove && (dName === toRemove || dName.includes(toRemove) || toRemove.includes(dName))) return false
        return true
      })
      const updatedCase = { ...caseReportModel.case, caseNumber: parsedCaseNumber, diagnosis: updatedDiags }
      await CasesService.update(updatedCase)
      setCaseReportModel({ ...caseReportModel, case: updatedCase })
      try {
        await CasesService.auditSmartReviewSuggestion(caseNumber, caseReportModel.reportNumber, {
          fieldId: suggestion.fieldId,
          suggestionsType: suggestion.suggestionsType,
          ruleName: suggestion.ruleName,
          originalText: suggestion.originalText,
          proposedText: 'Remove',
          finalText: 'Removed',
          isOverridden: true,
        })
      } catch (auditErr) {
        // eslint-disable-next-line no-console
        console.warn('Failed to audit smart review remove diagnosis', auditErr)
      }
      markSuggestionResolved(index, 'applied')
      await refreshReport()
      cogoToast.success('Diagnosis removed!')
    } catch (err) {
      cogoToast.error('Failed to remove diagnosis.')
    }
  }

  const handleAcceptAllSuggestions = async () => {
    const unresolves = smartReviewSuggestions.filter(s => !s.resolved && s.proposedText)
    if (unresolves.length === 0) {
      cogoToast.info('No remaining auto-applicable suggestions.')
      return
    }
    const progressToast = cogoToast.loading('Applying all suggestions...', { hideAfter: 0 })
    try {
      const parsedCaseNumber = parseInt(caseNumber, 10)
      for (const suggestion of unresolves) {
        if (suggestion.fieldId === 'report.overview') {
          const reportNum = caseReportModel.reportNumber || 0
          await CasesService.updateReport(caseNumber, reportNum, { overview: suggestion.proposedText })
        } else if (suggestion.fieldId === 'case.injury.description' && suggestion.proposedText) {
          const descList = [...(caseReportModel.case?.injury?.descriptions || [])]
          if (descList.length > 0) {
            const matchIdx = suggestion.originalText
              ? descList.findIndex(d => (d?.description || '').includes(suggestion.originalText))
              : -1
            const targetIdx = matchIdx !== -1 ? matchIdx : descList.length - 1
            const curr = descList[targetIdx].description || ''
            const updatedText =
              suggestion.originalText && curr.includes(suggestion.originalText)
                ? curr.replace(suggestion.originalText, suggestion.proposedText)
                : suggestion.proposedText
            descList[targetIdx] = {
              ...descList[targetIdx],
              description: updatedText,
            }
          } else {
            descList.push({
              description: suggestion.proposedText,
              provider: '',
              date: null,
            })
          }
          const updatedCase = {
            ...caseReportModel.case,
            caseNumber: parsedCaseNumber,
            injury: {
              ...caseReportModel.case?.injury,
              descriptions: descList,
            },
          }
          await CasesService.update(updatedCase)
          setCaseReportModel({ ...caseReportModel, case: updatedCase })
        } else if (suggestion.fieldId.startsWith('client.')) {
          const field = suggestion.fieldId.split('.')[1]
          const updatedCase = {
            ...caseReportModel.case,
            caseNumber: parsedCaseNumber,
            [field]: suggestion.proposedText,
          }
          await CasesService.update(updatedCase)
        } else if (suggestion.fieldId?.startsWith('activity.')) {
          const actNum = parseInt(suggestion.fieldId.replace('activity.', '').split('.')[0], 10)
          const appts = caseReportModel.appointments || []
          const apptWithAct = appts.find(a => (a.activities || []).some(act => act.activityNumber === actNum))
          if (apptWithAct) {
            if (suggestion.proposedText === 'Remove') {
              const updatedActs = (apptWithAct.activities || []).filter(act => act.activityNumber !== actNum)
              await AppointmentsService.update(caseNumber, apptWithAct.appointmentNumber, {
                ...apptWithAct,
                activities: updatedActs,
              })
            } else if (suggestion.proposedText) {
              const updatedActs = (apptWithAct.activities || []).map(act =>
                act.activityNumber === actNum ? { ...act, description: suggestion.proposedText } : act,
              )
              await AppointmentsService.update(caseNumber, apptWithAct.appointmentNumber, {
                ...apptWithAct,
                activities: updatedActs,
              })
            }
          } else {
            const tasks = caseReportModel.tasks || []
            const taskWithAct = tasks.find(t => (t.activities || []).some(act => act.activityNumber === actNum))
            if (taskWithAct) {
              if (suggestion.proposedText === 'Remove') {
                const updatedActs = (taskWithAct.activities || []).filter(act => act.activityNumber !== actNum)
                await TasksService.update(
                  { ...taskWithAct, activities: updatedActs },
                  caseNumber,
                  taskWithAct.taskNumber,
                )
              } else if (suggestion.proposedText) {
                const updatedActs = (taskWithAct.activities || []).map(act =>
                  act.activityNumber === actNum ? { ...act, description: suggestion.proposedText } : act,
                )
                await TasksService.update(
                  { ...taskWithAct, activities: updatedActs },
                  caseNumber,
                  taskWithAct.taskNumber,
                )
              }
            }
          }
        }
        try {
          await CasesService.auditSmartReviewSuggestion(caseNumber, caseReportModel.reportNumber, {
            fieldId: suggestion.fieldId,
            suggestionsType: suggestion.suggestionsType,
            ruleName: suggestion.ruleName,
            originalText: suggestion.originalText,
            proposedText: suggestion.proposedText,
            finalText: suggestion.proposedText,
            isOverridden: false,
          })
        } catch (auditErr) {
          // eslint-disable-next-line no-console
          console.warn('Failed to audit smart review suggestion in accept all', auditErr)
        }
      }
      setSmartReviewSuggestions(prev =>
        prev.map(s => (s.proposedText ? { ...s, resolved: true, outcome: 'applied' } : s)),
      )
      await refreshReport()
      progressToast.hide()
      cogoToast.success('All suggestions applied successfully!')
    } catch (err) {
      progressToast.hide()
      cogoToast.error('Error applying some suggestions.')
    }
  }

  useEffect(() => {
    const currentSuggestion = smartReviewSuggestions[activeSuggestionIndex]
    if (currentSuggestion?.fieldId) {
      setTimeout(() => {
        const getTargetElement = fieldId => {
          if (!fieldId) return null
          // Direct ID match first
          const directEl = document.getElementById(fieldId)
          if (directEl) return directEl

          const id = fieldId.toLowerCase()
          if (id.startsWith('diagnosis.')) {
            return document.getElementById('case.diagnoses')
          }
          if (id.startsWith('provider.')) {
            return document.getElementById('case.treatmentProviders')
          }
          if (id.startsWith('activity.')) {
            return document.getElementById('report.activities')
          }
          if (id.startsWith('appointment.')) {
            return document.getElementById('report.activities')
          }
          if (id.includes('allerg')) {
            return document.getElementById('case.allergies')
          }
          if (id.includes('medicat')) {
            return document.getElementById('case.medications')
          }
          if (id.includes('test')) {
            return document.getElementById('case.tests')
          }
          if (id.includes('injury')) {
            return document.getElementById('case.injury.description') || document.getElementById('case.injury.date')
          }
          if (id.includes('status') || id.includes('work')) {
            return document.getElementById('case.status')
          }
          if (id.includes('mmi')) {
            return document.getElementById('case.mmi')
          }
          if (
            id.includes('firstname') ||
            id.includes('lastname') ||
            id.includes('client.name') ||
            id.includes('claimant')
          ) {
            return document.getElementById('client.name')
          }
          if (id.includes('address')) {
            return document.getElementById('client.address')
          }
          if (id.includes('referral.date') || id.includes('referraldate')) {
            return document.getElementById('referral.date')
          }
          if (id.includes('overview') || id.includes('report.overview')) {
            return document.getElementById('report.overview')
          }
          if (
            id.includes('currentmedicalstatus') ||
            id.includes('medicalstatus') ||
            id.includes('report.currentmedicalstatus')
          ) {
            return document.getElementById('report.currentMedicalStatus')
          }
          if (id.includes('activitydates') || id.includes('period') || id.includes('range')) {
            return document.getElementById('report.activityDates')
          }
          return document.getElementById(fieldId)
        }

        const el = getTargetElement(currentSuggestion.fieldId)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          el.classList.add('smart-review-flash')
          setTimeout(() => {
            el.classList.remove('smart-review-flash')
          }, 1500)
        }
      }, 300)
    }
  }, [activeSuggestionIndex, smartReviewSuggestions])

  useEffect(() => {
    let isMounted = true

    const caseManagerPromise = caseReportPromise.then(report => {
      // report.case is the Dictionary. caseManager should be the ID.
      // If report.case is normalized correctly, this should work.
      if (!report.case?.caseManager) return null
      return UserService.get(report.case.caseManager)
    })

    const referralSourcePromise = caseReportPromise.then(report => {
      if (!report.case?.referral?.sourceId) return null
      return ReferralSourcesService.get(report.case.referral.sourceId)
    })

    const adjusterPromise = caseReportPromise.then(report => {
      if (!report.case?.referral?.adjusterId) return null
      return AdjustersService.get(report.case.referral.adjusterId)
    })

    const appointmentsPromise = AppointmentsService.getCaseAppointments(caseNumber).catch(() => [])

    Promise.all([
      caseReportPromise,
      caseManagerPromise,
      filesPromise,
      referralSourcePromise,
      adjusterPromise,
      appointmentsPromise,
    ])
      .then(results => {
        if (!isMounted) return

        const report = results[0]
        setCaseReportModel(report)
        setCaseManager(results[1])
        setReferralSource(results[3])
        setAdjuster(results[4])
        setAllCaseAppointments(results[5] || [])
        try {
          if (report.smartReviewSuggestions) {
            setSmartReviewSuggestions(JSON.parse(report.smartReviewSuggestions).suggestions || [])
          } else {
            setSmartReviewSuggestions([])
          }
        } catch {
          setSmartReviewSuggestions([])
        }

        setIsLoading(false)
      })
      .catch(err => {
        if (isMounted) {
          setError(err)
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseReportPromise])

  if (error) {
    return <div className="text-danger text-center mt-4">Failed to load report. Please try again later.</div>
  }

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center mt-2">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    )
  }

  let activityExpensesExportReportFilterUrlPart = `filterOnReportNumber=${caseReportModel.reportNumber}`
  if (isInProgress) {
    activityExpensesExportReportFilterUrlPart = 'filterOnInProgressReport=true'
  }

  const activities = [...(caseReportModel.activities || [])]
  activities.sort((a, b) => (b.date > a.date ? 1 : -1))

  const expenses = [...(caseReportModel.miscExpenses || [])]
  expenses.sort((a, b) => (a.createdUtc > b.createdUtc ? 1 : -1))

  let statusTextClass = ''
  if (caseReportModel.approvedOnUtc) {
    statusTextClass = 'text-success'
  } else if (caseReportModel.rejectedOnUtc) {
    statusTextClass = 'text-danger'
  }

  const cs = settings?.caseStatuses
  const statusDisplayMap = {
    'in-progress': cs?.inProgress?.statusText,
    in_progress: cs?.inProgress?.statusText,
    draft: cs?.inProgress?.statusText,
    submitted: cs?.submitted?.statusText,
    'under-review': cs?.submitted?.statusText,
    under_review: cs?.submitted?.statusText,
    'in-review': cs?.submitted?.statusText,
    in_review: cs?.submitted?.statusText,
    'sent-to-administrator': cs?.submitted?.statusText,
    sent_to_administrator: cs?.submitted?.statusText,
    'sent-to-reviewer': cs?.submitted?.statusText,
    sent_to_reviewer: cs?.submitted?.statusText,
    approved: cs?.approved?.statusText,
    rejected: cs?.rejected?.statusText,
    closed: cs?.closed?.statusText,
    canceled: cs?.canceled?.statusText,
    filed: cs?.filed?.statusText,
    final: cs?.final?.statusText,
    invoiced: cs?.invoiced?.statusText,
    in_smart_review: 'In Smart Review',
    'in-smart-review': 'In Smart Review',
  }
  const isSmartReviewAllowed =
    caseReportModel &&
    (statusLower === 'draft' ||
      statusLower === 'in-progress' ||
      statusLower === 'in-smart-review' ||
      statusLower === 'under-review')

  const statusDisplayText =
    statusDisplayMap[statusLower] ||
    statusDisplayMap[statusLower.replace(/-/g, '_')] ||
    (caseReportModel.status || '').replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

  return (
    <div className="container ReportsPage-component pt-4 px-4">
      {isSaving && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="sr-only">Saving...</span>
          </div>
        </div>
      )}
      <Helmet
        style={[
          {
            cssText: `
          @media print {
            #report-case-details-section, #report-upcoming-appointments-section {
              page-break-after: always;
            }
            .details-section-subsection-card {
              border: none;
              border-radius: 0;
            }
            .card-body {
              border: 1px solid #dfdfdf;
            }
            .card-header {
              border: 1px solid #dfdfdf;
            }
            .details-section-subsection-card,
            .form-group,
            .single-line-list,
            .report-list-item {
              page-break-inside: avoid;
            }
            .bg-light {
              background-color : #fff !important;
            }
            .tab-bar-content-container,
            .nav-container {
              padding-top: 0 !important;
            }
            .navbar,
            .tab-bar-container,
            #reports-list-back-button,
            #submit-for-review-btn,
            #case-report-reject-btn,
            #case-report-approve-btn {
              display: none;
            }
          }
          .smart-review-highlight {
            box-shadow: 0 0 15px #ffc107 !important;
            border: 3px solid #ffc107 !important;
            background-color: #fffbeb !important;
            transition: all 0.3s ease-in-out !important;
          }
          @keyframes review-flash {
            0% { background-color: #ffe8a1; }
            100% { background-color: #fffbeb; }
          }
          .smart-review-flash {
            animation: review-flash 1.2s ease-in-out;
          }
          .wizard-border-responsive {
            border-right: 1px solid #dfdfdf;
          }
          @media (max-width: 767px) {
            .wizard-border-responsive {
              border-right: none !important;
              border-bottom: 1px solid #dfdfdf !important;
            }
          }
        `,
          },
        ]}
      />
      <div id="reports-list-back-button" className="mb-2">
        <Link to={`/cases/${caseNumber}/reports`}>
          <FAIcon name="chevron-left" /> All Case Reports
        </Link>
      </div>
      <div className="row">
        <div className="col-md-7">
          <div className="h4 d-inline-block" style={{ paddingTop: '0.25rem' }}>
            {caseReportModel.reportType === 'Final'
              ? 'Final Report'
              : caseReportModel.reportType === 'Initial'
              ? 'Initial Case Report'
              : caseReportModel.reportType === 'CaseManagement'
              ? 'Case Management Report'
              : `Progress Report ${caseReportModel.sequenceNumber}`}
          </div>
          <div className="btn-group" role="group">
            <OpenButton
              className={'btn btn-sm btn-link align-top'}
              defaultExtension="pdf"
              url={`/api/cases/${caseNumber}/reports/${caseReportModel.reportNumber}/download`}
            >
              <FAIcon name="print" /> Printable Report
            </OpenButton>

            {Permissions.can(user, CAPABILITIES.DOWNLOAD_PRODUCTIVITY_REPORT) && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="export-tooltip">
                    Download an Excel file containing the time and expenses for this date range
                  </Tooltip>
                }
              >
                <div className="d-inline-block">
                  {' '}
                  {/* Wrapper to ensure tooltip works on custom components if needed */}
                  <DownloadButton
                    className={'btn btn-sm btn-link align-top'}
                    defaultExtension="xlsx"
                    url={`/api/productivity-report/download?filterCaseNumber=${caseNumber}&${activityExpensesExportReportFilterUrlPart}`}
                  >
                    <FAIcon name="download" /> Download Productivity Report
                  </DownloadButton>
                </div>
              </OverlayTrigger>
            )}
          </div>
        </div>
        <div className="col-md-5 text-right mb-2">
          {actionsComponent &&
            (caseReportModel.reportNumber > 1 || caseReportModel.reportType !== 'Initial') &&
            statusLower !== 'final' &&
            actionsComponent({ reportModel: caseReportModel })}
        </div>
      </div>

      <div className="row">
        {/* Left Column: Report preview and summaries */}
        <div className={isSmartReviewAllowed ? 'col-lg-8' : 'col-12'}>
          <div className="row mb-4">
            <div className="col-md-8">
              <div className="form-group">
                <label className="form-label text-uppercase mb-1" htmlFor="status">
                  Status
                </label>
                <h4
                  id="status"
                  className={`${statusTextClass} text-default-print m-0 font-weight-bold d-flex align-items-center`}
                >
                  <span>{statusDisplayText}</span>
                  {statusLower === 'approved' && (
                    <span
                      className="badge ml-2 font-weight-normal text-dark"
                      style={{
                        backgroundColor: '#fff3cd',
                        borderColor: '#ffeeba',
                        border: '1px solid #ffeeba',
                        fontSize: '13px',
                      }}
                    >
                      Awaiting Invoicing
                    </span>
                  )}
                </h4>
                {statusLower === 'approved' && (
                  <div className="text-muted small mt-1">Awaiting AR invoice number to complete invoicing.</div>
                )}
              </div>
              {caseReportModel.rejectedReason && (
                <div className="mt-3 p-3 bg-light border-left border-danger text-danger">
                  <strong>Reason for Rejection:</strong> {caseReportModel.rejectedReason}
                </div>
              )}
            </div>

            <div className="col-md-4">
              <CaseSummary
                caseSummaryModel={caseReportModel}
                reportHoursTarget={settings.reportHoursTarget}
                reportDaysTarget={settings.reportDaysTarget}
              />
            </div>
          </div>

          <InteractiveReportPreview
            caseReportModel={caseReportModel}
            caseManager={caseManager}
            referralSource={referralSource}
            adjuster={adjuster}
            settings={settings}
            onFieldClick={handleFieldClick}
            activeFieldId={smartReviewSuggestions[activeSuggestionIndex]?.fieldId}
            allCaseAppointments={allCaseAppointments}
            isReadOnly={!isEditableReport}
          />
        </div>

        {/* Right Column: AI Smart Review Control Pane */}
        {isSmartReviewAllowed && (
          <div className="col-lg-4 pl-lg-3">
            <div
              className="sticky-top"
              style={{
                top: `${stickyTopOffset}px`,
                zIndex: 9,
                maxHeight: `calc(100vh - ${stickyTopOffset + 16}px)`,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                className="card border-info shadow mb-4"
                style={{
                  maxHeight: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <div
                  className="card-header bg-info text-white d-flex justify-content-between align-items-center"
                  style={{ flexShrink: 0 }}
                >
                  <h5 className="mb-0 h6 font-weight-bold">
                    <i className="fa fa-magic mr-2" /> Smart Review
                  </h5>
                  <div className="d-flex align-items-center">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="rerun-tooltip">
                          Start the Smart Review over again including all edits you have applied so far
                        </Tooltip>
                      }
                    >
                      <button
                        className="btn btn-xs btn-light font-weight-bold mr-2"
                        onClick={handleTriggerSmartReview}
                        disabled={isTriggeringReview}
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      >
                        {isTriggeringReview ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm mr-1"
                              role="status"
                              aria-hidden="true"
                              style={{ width: '10px', height: '10px' }}
                            />
                            Analyzing...
                          </>
                        ) : smartReviewSuggestions.length > 0 ? (
                          'Re-run'
                        ) : (
                          'Trigger'
                        )}
                      </button>
                    </OverlayTrigger>

                    <button
                      className="btn btn-xs btn-outline-light font-weight-bold"
                      onClick={() => setShowCancelConfirm(true)}
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', borderWidth: '1px' }}
                      title="Cancel Smart Review and return to In Progress"
                    >
                      Cancel
                    </button>
                  </div>
                </div>

                {smartReviewSuggestions.length > 0 ? (
                  <div
                    className="card-body bg-white p-3 d-flex flex-column"
                    style={{
                      overflow: 'hidden',
                      flex: '1 1 auto',
                      minHeight: 0,
                    }}
                  >
                    <div style={{ flexShrink: 0 }}>
                      {/* Step selector dropdown */}
                      <div className="form-group mb-2">
                        <label className="form-label text-muted small mb-1 font-weight-bold">
                          Active Suggestion Step
                        </label>
                        <select
                          className="form-control form-control-sm font-weight-bold"
                          value={activeSuggestionIndex}
                          onChange={e => setActiveSuggestionIndex(parseInt(e.target.value, 10))}
                          style={{ border: '1px solid #17a2b8' }}
                        >
                          {smartReviewSuggestions.map((s, idx) => (
                            <option key={idx} value={idx}>
                              {idx + 1}. {s.resolved ? '✓ ' : '⚠️ '}
                              {s.ruleName}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Visual Progress Bar */}
                      <div className="d-flex justify-content-between align-items-center mb-1 small text-muted">
                        <span>Progress</span>
                        <span>
                          {Math.round(
                            (smartReviewSuggestions.filter(s => s.resolved).length / smartReviewSuggestions.length) *
                              100,
                          )}
                          % Complete
                        </span>
                      </div>
                      <div className="progress mb-2" style={{ height: '5px', borderRadius: '2px' }}>
                        <div
                          className="progress-bar bg-success"
                          role="progressbar"
                          style={{
                            width: `${
                              (smartReviewSuggestions.filter(s => s.resolved).length / smartReviewSuggestions.length) *
                              100
                            }%`,
                          }}
                        />
                      </div>

                      {/* Accept All suggestions shortcut */}
                      {smartReviewSuggestions.some(s => !s.resolved && s.proposedText) && (
                        <div className="text-right mb-2">
                          <button
                            type="button"
                            className="btn btn-link btn-xs text-info p-0 font-weight-bold"
                            style={{ fontSize: '0.75rem', textDecoration: 'none', outline: 'none' }}
                            onClick={handleAcceptAllSuggestions}
                          >
                            Accept All Suggestions
                          </button>
                        </div>
                      )}
                    </div>

                    {(() => {
                      const suggestion = smartReviewSuggestions[activeSuggestionIndex]
                      if (!suggestion) return null

                      let activityContext = null
                      if (suggestion.fieldId?.startsWith('activity.')) {
                        const actNum = parseInt(suggestion.fieldId.replace('activity.', '').split('.')[0], 10)
                        const actItem = (caseReportModel.activities || []).find(a => a.activityNumber === actNum)
                        const apptNum = actItem?.appointmentNumber || caseReportModel.activityToApptLookup?.[actNum]
                        const taskNum = actItem?.taskNumber || caseReportModel.activityToTaskLookup?.[actNum]
                        const parentAppt = (caseReportModel.appointments || []).find(
                          a => a.appointmentNumber === apptNum,
                        )
                        const parentTask = (caseReportModel.tasks || []).find(t => t.taskNumber === taskNum)

                        activityContext = (
                          <div
                            className="text-secondary small mb-2 d-flex align-items-center bg-light p-2 rounded border"
                            style={{ fontSize: '0.8rem' }}
                          >
                            <i className="fas fa-calendar-check text-info mr-1" />
                            <span>
                              <strong>Activity #{actNum}:</strong>&nbsp;
                              {actItem?.type || 'Activity'}
                              {actItem?.time != null ? ` (${actItem.time} hr${actItem.time === 1 ? '' : 's'})` : ''}
                              {parentAppt ? (
                                <span>
                                  &nbsp;•&nbsp;<strong>Appointment #{parentAppt.appointmentNumber}</strong>
                                  {parentAppt.title ? ` (${parentAppt.title})` : ''}
                                  {parentAppt.startUtc
                                    ? ` on ${moment.utc(parentAppt.startUtc).format('MM/DD/YYYY')}`
                                    : ''}
                                </span>
                              ) : apptNum ? (
                                <span>
                                  &nbsp;•&nbsp;<strong>Appointment #{apptNum}</strong>
                                </span>
                              ) : parentTask ? (
                                <span>
                                  &nbsp;•&nbsp;<strong>Task #{parentTask.taskNumber}</strong>
                                  {parentTask.title ? ` (${parentTask.title})` : ''}
                                </span>
                              ) : taskNum ? (
                                <span>
                                  &nbsp;•&nbsp;<strong>Task #{taskNum}</strong>
                                </span>
                              ) : null}
                            </span>
                          </div>
                        )
                      }

                      return (
                        <div
                          className="suggestion-detail d-flex flex-column"
                          style={{
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            className="suggestion-scroll-body pr-1"
                            style={{
                              overflowY: 'auto',
                              flex: '1 1 auto',
                              minHeight: 0,
                            }}
                          >
                            {suggestion.resolved ? (
                              <div className="text-center py-4 my-2 border rounded bg-light">
                                <i className="fa fa-check-circle text-success mb-2" style={{ fontSize: '2rem' }} />
                                <h6 className="font-weight-bold text-success" style={{ fontSize: '0.95rem' }}>
                                  Suggestion Resolved
                                </h6>
                                <p className="text-muted small mb-2" style={{ fontSize: '0.85rem' }}>
                                  Action:{' '}
                                  <strong>
                                    {suggestion.outcome === 'applied'
                                      ? 'Correction Applied'
                                      : suggestion.outcome === 'edited'
                                      ? 'Edited Manually'
                                      : 'Ignored'}
                                  </strong>
                                </p>
                                <button
                                  className="btn btn-xs btn-outline-secondary"
                                  onClick={() => {
                                    setSmartReviewSuggestions(prev => {
                                      const copy = [...prev]
                                      copy[activeSuggestionIndex] = { ...copy[activeSuggestionIndex] }
                                      delete copy[activeSuggestionIndex].resolved
                                      delete copy[activeSuggestionIndex].outcome
                                      return copy
                                    })
                                  }}
                                  style={{ fontSize: '0.75rem', padding: '0.2rem 0.4rem' }}
                                >
                                  Undo Action
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span
                                    className={`badge ${
                                      suggestion.suggestionsType === 'warning'
                                        ? 'badge-warning text-dark'
                                        : 'badge-primary'
                                    } font-weight-bold`}
                                  >
                                    {suggestion.suggestionsType.toUpperCase()}
                                  </span>
                                </div>

                                {activityContext}

                                <div
                                  className="alert alert-warning border-warning text-dark p-2 mb-2"
                                  style={{ fontSize: '0.85rem', lineHeight: '1.3', backgroundColor: '#fffbeb' }}
                                >
                                  {suggestion.reason}
                                </div>

                                {suggestion.proposedText &&
                                  suggestion.proposedText !== 'Remove' &&
                                  suggestion.proposedText !== 'Delete' &&
                                  suggestion.ruleName !== 'Date Inconsistency' &&
                                  !/^verify\b|^check\b/i.test(suggestion.proposedText) && (
                                    <div
                                      className="bg-light p-2 rounded mb-2 font-mono small border"
                                      style={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.8rem',
                                        overflowX: 'auto',
                                        whiteSpace: 'pre-wrap',
                                      }}
                                    >
                                      {suggestion.originalText ? (
                                        <>
                                          <div
                                            className="text-muted mb-1 font-weight-bold"
                                            style={{ fontSize: '0.75rem' }}
                                          >
                                            Changes:
                                          </div>
                                          <div className="text-danger mb-1" style={{ textDecoration: 'line-through' }}>
                                            - {suggestion.originalText}
                                          </div>
                                          <div className="text-success font-weight-bold">
                                            + {suggestion.proposedText}
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div
                                            className="text-muted mb-1 font-weight-bold"
                                            style={{ fontSize: '0.75rem' }}
                                          >
                                            Proposed Text:
                                          </div>
                                          <div className="text-success font-weight-bold">
                                            + {suggestion.proposedText}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  )}

                                {suggestion.ruleName === 'Negative Finding' ||
                                suggestion.ruleName === 'Remove Negative Finding' ? (
                                  <div
                                    className="text-danger small mb-2 p-2 bg-light rounded border font-weight-bold"
                                    style={{ fontSize: '0.8rem' }}
                                  >
                                    <i className="fas fa-trash-alt mr-1" />
                                    Negative diagnostic result: Click <strong>Remove Finding</strong> to delete this
                                    entry from case diagnoses.
                                  </div>
                                ) : suggestion.ruleName === 'Pre-Existing Condition' ? (
                                  <div
                                    className="text-danger small mb-2 p-2 bg-light rounded border font-weight-bold"
                                    style={{ fontSize: '0.8rem' }}
                                  >
                                    <i className="fas fa-trash-alt mr-1" />
                                    Pre-existing / non-industrial condition: Click <strong>Remove Diagnosis</strong> to
                                    delete this entry from case diagnoses, or <strong>Ignore</strong> to keep it.
                                  </div>
                                ) : suggestion.ruleName === 'Remove Contradictory Allergy' ||
                                  suggestion.ruleName === 'Allergy Contradiction' ? (
                                  <div
                                    className="text-danger small mb-2 p-2 bg-light rounded border font-weight-bold"
                                    style={{ fontSize: '0.8rem' }}
                                  >
                                    <i className="fas fa-trash-alt mr-1" />
                                    Contradictory allergy record: Click <strong>Delete</strong> below to remove it, or
                                    click the highlighted field in the preview to edit the allergy list.
                                  </div>
                                ) : suggestion.ruleName === 'Date Inconsistency' ? (
                                  <div
                                    className="small mb-2 p-2 bg-light rounded border font-weight-bold"
                                    style={{ fontSize: '0.8rem', color: '#856404' }}
                                  >
                                    <i className="fas fa-calendar-alt mr-1" />
                                    Date inconsistency: Click <strong>Edit Activity</strong> to adjust the date, hours,
                                    or description, or click <strong>Ignore</strong>.
                                  </div>
                                ) : suggestion.ruleName === 'Incomplete Activity' ? (
                                  <div
                                    className="text-danger small mb-2 p-2 bg-light rounded border font-weight-bold"
                                    style={{ fontSize: '0.8rem' }}
                                  >
                                    <i className="fas fa-exclamation-triangle mr-1" />
                                    Incomplete activity with 0 billable hours: Click <strong>
                                      Remove Activity
                                    </strong>{' '}
                                    below, or click <strong>Edit Activity</strong> to add hours and description.
                                  </div>
                                ) : suggestion.ruleName === 'Missing Description' ||
                                  (suggestion.fieldId?.startsWith('activity.') &&
                                    suggestion.ruleName?.toLowerCase().includes('description')) ? (
                                  <div
                                    className="text-info small mb-2 p-2 bg-light rounded border font-weight-bold"
                                    style={{ fontSize: '0.8rem' }}
                                  >
                                    <i className="fas fa-edit mr-1" />
                                    Missing description: Click <strong>Edit Description</strong> below to enter details,
                                    or click the row in the table.
                                  </div>
                                ) : !suggestion.proposedText ? (
                                  <div
                                    className="text-muted small mb-2 p-2 bg-light rounded border italic"
                                    style={{ fontSize: '0.8rem' }}
                                  >
                                    💡 Click the highlighted field in the document preview to edit in-place, then click{' '}
                                    <strong>Ignore</strong>.
                                  </div>
                                ) : null}
                              </>
                            )}
                          </div>

                          <div
                            className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top"
                            style={{ flexShrink: 0 }}
                          >
                            <div>
                              <button
                                className="btn btn-sm btn-outline-dark font-weight-bold mr-1"
                                onClick={() => setActiveSuggestionIndex(0)}
                                disabled={activeSuggestionIndex === 0}
                                title="Go to First Suggestion (Top)"
                                style={{ padding: '0.25rem 0.4rem' }}
                              >
                                ≪ Top
                              </button>
                              <button
                                className="btn btn-sm btn-outline-dark font-weight-bold mr-1"
                                onClick={() => setActiveSuggestionIndex(prev => Math.max(0, prev - 1))}
                                disabled={activeSuggestionIndex === 0}
                                style={{ padding: '0.25rem 0.5rem' }}
                              >
                                ◄ Prev
                              </button>
                              <button
                                className="btn btn-sm btn-outline-dark font-weight-bold mr-1"
                                onClick={() =>
                                  setActiveSuggestionIndex(prev =>
                                    Math.min(smartReviewSuggestions.length - 1, prev + 1),
                                  )
                                }
                                disabled={activeSuggestionIndex === smartReviewSuggestions.length - 1}
                                style={{ padding: '0.25rem 0.5rem' }}
                              >
                                Next ►
                              </button>
                              <button
                                className="btn btn-sm btn-outline-dark font-weight-bold"
                                onClick={() => setActiveSuggestionIndex(smartReviewSuggestions.length - 1)}
                                disabled={activeSuggestionIndex === smartReviewSuggestions.length - 1}
                                title="Go to Last Suggestion (Bottom)"
                                style={{ padding: '0.25rem 0.4rem' }}
                              >
                                Bottom ≫
                              </button>
                            </div>
                            {!suggestion.resolved && (
                              <div>
                                <button
                                  className="btn btn-sm btn-danger text-white font-weight-bold mr-1"
                                  onClick={() => handleIgnoreSuggestion(suggestion, activeSuggestionIndex)}
                                  style={{ padding: '0.25rem 0.6rem', fontWeight: 'bold' }}
                                >
                                  Ignore
                                </button>
                                {suggestion.ruleName === 'Negative Finding' ||
                                suggestion.ruleName === 'Remove Negative Finding' ? (
                                  <button
                                    className="btn btn-sm btn-warning text-dark font-weight-bold"
                                    onClick={() => handleAcceptSuggestion(suggestion, activeSuggestionIndex)}
                                    style={{ padding: '0.25rem 0.6rem', fontWeight: 'bold' }}
                                  >
                                    Remove Finding
                                  </button>
                                ) : suggestion.ruleName === 'Consolidate Diagnoses' ? (
                                  <button
                                    className="btn btn-sm btn-success text-white font-weight-bold"
                                    onClick={() => handleAcceptSuggestion(suggestion, activeSuggestionIndex)}
                                    style={{
                                      padding: '0.25rem 0.6rem',
                                      fontWeight: 'bold',
                                      backgroundColor: '#28a745',
                                      borderColor: '#28a745',
                                    }}
                                  >
                                    Consolidate
                                  </button>
                                ) : suggestion.ruleName === 'Pre-Existing Condition' ? (
                                  <button
                                    className="btn btn-sm btn-danger text-white font-weight-bold"
                                    onClick={() => handleRemoveDiagnosisSuggestion(suggestion, activeSuggestionIndex)}
                                    style={{ padding: '0.25rem 0.6rem', fontWeight: 'bold' }}
                                  >
                                    Remove Diagnosis
                                  </button>
                                ) : suggestion.ruleName === 'Remove Contradictory Allergy' ||
                                  suggestion.ruleName === 'Allergy Contradiction' ||
                                  (suggestion.fieldId === 'case.allergies' &&
                                    (suggestion.proposedText === 'Delete' || suggestion.proposedText === 'Remove')) ? (
                                  <button
                                    className="btn btn-sm btn-danger text-white font-weight-bold"
                                    onClick={() => handleAcceptSuggestion(suggestion, activeSuggestionIndex)}
                                    style={{ padding: '0.25rem 0.6rem', fontWeight: 'bold' }}
                                  >
                                    Delete{' '}
                                    {suggestion.originalText
                                      ? `'${suggestion.originalText.replace(/^['"`]+|['"`]+$/g, '')}'`
                                      : 'Allergy'}
                                  </button>
                                ) : suggestion.ruleName === 'Incomplete Activity' ||
                                  (suggestion.fieldId?.startsWith('activity.') &&
                                    suggestion.proposedText === 'Remove') ? (
                                  <>
                                    <button
                                      className="btn btn-sm btn-outline-primary font-weight-bold mr-1"
                                      onClick={() => handleEditActivityFromSuggestion(suggestion)}
                                      style={{ padding: '0.25rem 0.6rem', fontWeight: 'bold' }}
                                      title="Open activity editor to enter hours and description"
                                    >
                                      <i className="fas fa-edit mr-1" />
                                      Edit Activity
                                    </button>
                                    <button
                                      className="btn btn-sm btn-danger text-white font-weight-bold"
                                      onClick={() => handleAcceptSuggestion(suggestion, activeSuggestionIndex)}
                                      style={{ padding: '0.25rem 0.6rem', fontWeight: 'bold' }}
                                    >
                                      Remove Activity
                                    </button>
                                  </>
                                ) : suggestion.fieldId?.startsWith('activity.') ? (
                                  <>
                                    <button
                                      className="btn btn-sm btn-outline-primary font-weight-bold mr-1"
                                      onClick={() => handleEditActivityFromSuggestion(suggestion)}
                                      style={{ padding: '0.25rem 0.6rem', fontWeight: 'bold' }}
                                      title={
                                        suggestion.ruleName?.toLowerCase().includes('date')
                                          ? 'Open activity editor to modify date'
                                          : suggestion.ruleName?.toLowerCase().includes('description')
                                          ? 'Open activity editor to enter or modify description'
                                          : 'Open activity editor'
                                      }
                                    >
                                      <i className="fas fa-edit mr-1" />
                                      {suggestion.ruleName?.toLowerCase().includes('date')
                                        ? 'Edit Activity Date'
                                        : suggestion.ruleName?.toLowerCase().includes('description')
                                        ? 'Edit Description'
                                        : 'Edit Activity'}
                                    </button>
                                    {suggestion.proposedText &&
                                      suggestion.ruleName?.toLowerCase().includes('description') &&
                                      !/^verify\b|^check\b/i.test(suggestion.proposedText) && (
                                        <button
                                          className="btn btn-sm btn-success text-white font-weight-bold"
                                          onClick={() => handleAcceptSuggestion(suggestion, activeSuggestionIndex)}
                                          style={{
                                            padding: '0.25rem 0.6rem',
                                            fontWeight: 'bold',
                                            backgroundColor: '#28a745',
                                            borderColor: '#28a745',
                                          }}
                                        >
                                          Apply Description
                                        </button>
                                      )}
                                  </>
                                ) : suggestion.ruleName === 'Provider Merge' ? (
                                  <button
                                    className="btn btn-sm btn-primary text-white font-weight-bold"
                                    onClick={() => handleAcceptSuggestion(suggestion, activeSuggestionIndex)}
                                    style={{ padding: '0.25rem 0.6rem', fontWeight: 'bold' }}
                                  >
                                    Merge Providers
                                  </button>
                                ) : suggestion.proposedText &&
                                  suggestion.ruleName !== 'Date Inconsistency' &&
                                  !/^verify\b|^check\b/i.test(suggestion.proposedText) ? (
                                  <button
                                    className="btn btn-sm btn-success text-white font-weight-bold"
                                    onClick={() => handleAcceptSuggestion(suggestion, activeSuggestionIndex)}
                                    style={{
                                      padding: '0.25rem 0.6rem',
                                      fontWeight: 'bold',
                                      backgroundColor: '#28a745',
                                      borderColor: '#28a745',
                                    }}
                                  >
                                    Apply
                                  </button>
                                ) : null}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })()}
                  </div>
                ) : (
                  <div className="card-body text-center py-4 bg-white">
                    <i className="fa fa-magic text-info mb-3" style={{ fontSize: '2rem' }} />
                    <div>
                      <button
                        className="btn btn-sm btn-info font-weight-bold text-white shadow-sm"
                        onClick={handleTriggerSmartReview}
                        disabled={isTriggeringReview}
                      >
                        {isTriggeringReview ? (
                          <>
                            <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
                            Analyzing Report...
                          </>
                        ) : (
                          'Trigger AI Smart Review'
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {(!user || Permissions.can(user, CAPABILITIES.CASE_EDIT)) && (
                  <div
                    className="card-footer bg-light border-top py-2 px-3 d-flex align-items-center justify-content-between"
                    data-testid="report-billable-time-prompt"
                    style={{ flexShrink: 0 }}
                  >
                    <div className="d-flex align-items-center small text-dark font-weight-bold">
                      <i className="fas fa-stopwatch text-info mr-2" />
                      <span>Billable Report Prep:</span>
                    </div>
                    {reportTimeLogged ? (
                      <span className="badge badge-success py-1 px-2" data-testid="report-time-logged-badge">
                        <i className="fas fa-check mr-1" />
                        Logged {Number(reportReviewHours).toFixed(1)}h
                      </span>
                    ) : (
                      <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                        <button
                          type="button"
                          className="btn btn-xs btn-outline-secondary py-0 px-2"
                          data-testid="report-time-decrement"
                          onClick={() =>
                            setReportReviewHours(prev => Math.max(0.1, Math.round((prev - 0.1) * 10) / 10))
                          }
                        >
                          -
                        </button>
                        <span
                          className="font-weight-bold small px-1"
                          data-testid="report-time-hours"
                          style={{ minWidth: '36px', textAlign: 'center' }}
                        >
                          {Number(reportReviewHours).toFixed(1)}h
                        </span>
                        <button
                          type="button"
                          className="btn btn-xs btn-outline-secondary py-0 px-2"
                          data-testid="report-time-increment"
                          onClick={() =>
                            setReportReviewHours(prev => Math.min(8.0, Math.round((prev + 0.1) * 10) / 10))
                          }
                        >
                          +
                        </button>
                        <button
                          type="button"
                          className="btn btn-xs btn-info text-white font-weight-bold py-1 px-2 ml-1"
                          data-testid="log-report-time-btn"
                          disabled={isLoggingReportTime}
                          onClick={handleLogReportReviewTime}
                          style={{ fontSize: '0.75rem' }}
                        >
                          {isLoggingReportTime ? 'Logging...' : '+ Log Time'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {showEditModal && (
        <Modal show={true} onHide={() => setShowEditModal(false)}>
          <Modal.Header>
            <Modal.Title>Activity Dates</Modal.Title>
            <button
              type="button"
              className="close"
              onClick={() => setShowEditModal(false)}
              title="Cancel (discard changes)"
              style={{
                outline: 'none',
                background: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                fontWeight: '700',
                lineHeight: 1,
                color: '#000',
                opacity: '.5',
              }}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </Modal.Header>
          <Modal.Body>
            {editError && <div className="alert alert-danger">{editError}</div>}

            <div className="form-row">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label font-weight-bold">Start Date</label>
                  <DatePicker
                    name="editStartUtc"
                    value={editStartUtc}
                    onChange={e => setEditStartUtc(e.target.value)}
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label font-weight-bold">End Date</label>
                  <DatePicker
                    name="editEndUtc"
                    value={editEndUtc}
                    onChange={e => setEditEndUtc(e.target.value)}
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowEditModal(false)}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSaveMetadata} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </Modal.Footer>
        </Modal>
      )}

      <WYSIWYGEditModal
        show={showWYSIWYGModal}
        onHide={() => setShowWYSIWYGModal(false)}
        title={wysiwygFieldTitle}
        type={wysiwygFieldType}
        value={wysiwygCurrentValue}
        onSave={handleSaveWYSIWYG}
        caseModel={caseReportModel?.case}
        fieldKey={wysiwygFieldKey}
        focusField={wysiwygFocusField}
        caseNumber={caseNumber}
        reportNumber={caseReportModel?.reportNumber}
        onRefresh={refreshReport}
      />

      {showCancelConfirm && (
        <ConfirmationModal
          title="Cancel Smart Review"
          body="Are you sure you want to cancel the Smart Review? This will undo the current Smart Review session and return the report to its previous status."
          confirmText="Yes, Cancel"
          cancelText="No, Keep Reviewing"
          onConfirm={handleCancelSmartReview}
          onClose={() => setShowCancelConfirm(false)}
        />
      )}
    </div>
  )
}
